<template>
  <div
    ref="myElement"
    class="w-full"
    :data-testid="props.context.attrs['data-testid']">
    <el-date-picker
      v-model="date"
      type="date"
      placeholder="dd/mm/yyyy"
      :clearable="false"
      :editable="false"
      format="DD/MM/YYYY"
      popper-class="rounded-12 font-main"
      :popper-options="{
        placement: calendarPlacement,
      }"
      value-format="YYYY-MM-DD"/>
  </div>
</template>

<script lang="ts" setup>
import { FormKitFrameworkContext } from '@formkit/core';
import { debounce } from 'lodash';
import {
  onMounted, onUnmounted, ref, watch,
} from 'vue';

import { CALENDAR_HEIGHT } from '@/constants/calendar-height';

const props = defineProps<{ context: FormKitFrameworkContext }>();
const date = ref(props.context.value || '');
const myElement = ref<HTMLElement | null>(null);
const calendarPlacement = ref<'bottom' | 'top'>('bottom');
let scrollContainer: HTMLElement | Window = window;
let observer: IntersectionObserver | null = null;

const logElementPosition = debounce(() => {
  const element = myElement.value;
  if (element) {
    const {
      top, height,
    } = element.getBoundingClientRect();
    const calendarBottomOffset = window.innerHeight - top - height;
    calendarPlacement.value = calendarBottomOffset < CALENDAR_HEIGHT ? 'top' : 'bottom';
  }
}, 200);

function getScrollableParent(element: HTMLElement | null): HTMLElement | Window {
  if (!element) return window;
  let parent = element.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
}

function enableScrollListener() {
  scrollContainer.addEventListener('scroll', logElementPosition);
}
function disableScrollListener() {
  scrollContainer.removeEventListener('scroll', logElementPosition);
}

onMounted(() => {
  scrollContainer = getScrollableParent(myElement.value);
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        logElementPosition();
        enableScrollListener();
      } else {
        disableScrollListener();
      }
    },
    { threshold: 0.1 },
  );
  if (myElement.value) {
    observer.observe(myElement.value);
  }
  window.addEventListener('resize', logElementPosition);
});

onUnmounted(() => {
  observer?.disconnect();
  disableScrollListener();
  window.removeEventListener('resize', logElementPosition);
});
watch(date, () => {
  props.context.node.input(date.value);
});
</script>

<style lang="scss">
[data-type="DateInput"] {
  .el-input {
    @apply border-none w-full h-48;

    &__wrapper,
    &__inner {
      @apply border-none shadow-none text-old-black;

      &.is-focus,
      &:focus {
        @apply border-none shadow-none #{!important};
      }
    }

    &__inner {
      @apply px-16 font-normal text-lg #{!important};
    }

    &__prefix {
      @apply left-initial right-16 #{!important};
    }
  }

  .el-icon {
    @apply text-old-black mr-0 #{!important};
  }
}
</style>
